import React, { ReactNode, useEffect } from "react";
import { Params, SourceData, TableInterface } from "./Util/Interface";
import { extractHTMLText } from "./Util/Function";
import "./Styles/App.css";
import Pagination from "./Pagination_Style/Pagination";
import { defaultPageOptions } from "./Util/Constant";

export default function TableView({
  wrapper_class = "",
  action_container = "",
  custom_pre_action_start = null,
  custom_post_action_start = null,
  custom_pre_action_end = null,
  custom_post_action_end = null,
  action_container_start_class = "",
  action_container_end_class = "",
  action__container_start_action_icon = null,
  action_container_start_action_label = "",
  action__container_start_action_class = "",
  enableAction = false,
  handleActionClick = () => undefined,
  filter = false,
  filterLabel = "Filter",
  filterLabelClass = "",
  filterList = [],
  filterListClass = "",
  filterValue = "",
  onFilterSelect = () => undefined,
  filterView = null,
  search = false,
  searchData = "",
  onSearch = () => undefined,
  searchView = null,
  searchInstant = false,
  searchType = "default",
  searchMode = "default",
  searchKeys = [],
  searchTrigger = "click",
  searchFieldClass = "",
  check = false,
  globalPlacement = "left",
  headers = [],
  dataSource = [],
  tableType = "default",
  tableClass = "",
  tableHeaderClass = "",
  headerType = "stick",
  selectedRows = [],
  onRowSelect = () => undefined,
  selectMatchKey = "id",
  params = {
    page: 1,
    per_page: 25,
    sort_by: "id",
    sort_order: "desc",
  },
  pagination = true,
  updateParams = () => undefined,
  paginationPlacement = "right",
  paginationType = "default",
  tableContainerWrapperClass = "",
  clampColumnMessage = "",
  showMove = false,
  onUpdateOrder = () => undefined,
  paginationWrapperClass = "",
  paginationStyle = "classic",
  loading = false,
  loadRender = null,
  loaderClass = "",
  prevText = "",
  nextText = "",
  prevIcon = null,
  nextIcon = null,
  paginationButtonClass = "",
  pageOptions = defaultPageOptions,
  start_stick = true,
  end_stick = true,
  stripe = true,
  groupView = false,
  groupArrowClass = "",
  groupAction = true,
  groupViewClass = "",
  groupRender,
  groupCheck = false,
  groupColPadding = 16,
}: TableInterface) {
  const [expanded, setExpanded] = React.useState<{
    id: number | null;
    key: string;
    state: boolean;
  }>({ id: null, key: "", state: false });
  const [searchDataState, setSearchDataState] = React.useState<SourceData[]>(
    []
  );
  const [searchString, setSearchString] = React.useState<string>(searchData);
  const [refresh, setRefresh] = React.useState(false);
  const [groupViewPanel, setGroupViewPanel] = React.useState<boolean>(false);
  const [groupOpenIndex, setGroupOpenIndex] = React.useState<number>(-1);
  const [tempData, setTempData] = React.useState<SourceData[]>(dataSource);
  const [tempParams, setTempParams] = React.useState<Params>(
    params || {
      page: 1,
      per_page: 25,
      sort_by: "id",
      sort_order: "desc",
    }
  );

  useEffect(() => {
    if (!groupAction) setGroupViewPanel(groupView);
    if (!Array.isArray(dataSource)) return;

    if (typeof window !== "undefined") {
      if (paginationType === "inList") {
        setTempData(dataSource);
        setSearchDataState(
          dataSource.slice(
            ((tempParams?.page || 1) - 1) * (tempParams?.per_page || 25),
            (tempParams?.page || 1) * (tempParams?.per_page || 25)
          )
        );
        setTempParams((prev) =>
          prev.total === dataSource.length
            ? prev
            : { ...prev, total: dataSource.length }
        );
      } else {
        setSearchDataState((prev) => {
          if (prev === dataSource) return prev;
          if (
            Array.isArray(prev) &&
            Array.isArray(dataSource) &&
            prev.length === dataSource.length &&
            prev.every((v, i) => v === dataSource[i])
          ) {
            return prev;
          }
          return dataSource;
        });
      }
    }
  }, [
    dataSource,
    paginationType,
    tempParams?.page,
    tempParams?.per_page,
    groupView,
    groupAction,
  ]);

  const searchRef = React.useRef<number | null>(null);
  function handleSearch(searchStr: string) {
    setSearchString(searchStr);
    if (searchMode === "inline" || paginationType === "inList") {
      if (dataSource.length > 0) {
        const searchList = dataSource.filter((item) =>
          searchKeys.length > 0
            ? searchKeys.some((key) =>
                item[key]
                  ?.toString()
                  .toLowerCase()
                  .includes(searchStr.toLowerCase())
              )
            : headers.some((header) =>
                item[header?.key ?? ""]
                  ?.toString()
                  .toLowerCase()
                  .includes(searchStr.toLowerCase())
              )
        );
        setSearchDataState(searchList.slice(0, 25));
        setTempParams((prev) => {
          if (
            prev.page === 1 &&
            prev.per_page === 25 &&
            prev.total === searchList.length
          ) {
            return prev;
          }
          return { page: 1, per_page: 25, total: searchList.length };
        });
      } else {
        setSearchDataState([]);
        setTempParams({ page: 1, per_page: 25, total: 0 });
      }

      return;
    }
    if (searchInstant) {
      onSearch(searchStr);
    } else {
      if (searchRef.current) {
        clearTimeout(searchRef.current);
      }

      searchRef.current = window.setTimeout(() => {
        onSearch(searchStr);
      }, 1000);
    }
  }

  function handlePagination(paramsData: Params) {
    if (paginationType === "inList") {
      setSearchDataState(
        tempData.slice(
          paramsData?.page === 1
            ? 0
            : ((paramsData?.page || 1) - 1) * (paramsData?.per_page || 25),
          (paramsData?.page || 1) * (paramsData?.per_page || 25)
        )
      );
      setTempParams(paramsData);
      return;
    }

    updateParams(paramsData);
    setExpanded({ id: null, key: "", state: false });
  }

  function handleSorting(key: string, sort: "asc" | "desc") {
    if (paginationType === "inList") {
      const onlyDigits = /^\d+$/;

      searchDataState.sort((a: SourceData, b: SourceData) => {
        let aVal = (a[key] ?? "").toString();
        let bVal = (b[key] ?? "").toString();
        if (onlyDigits.test(aVal) && onlyDigits.test(bVal)) {
          return sort === "asc"
            ? parseInt(bVal) - parseInt(aVal)
            : parseInt(aVal) - parseInt(bVal);
        }
        return sort === "asc"
          ? bVal.localeCompare(aVal)
          : aVal.localeCompare(bVal);
      });

      setTempParams({
        ...tempParams,
        sort_by: key,
        sort_order: sort,
      });
    } else {
      updateParams({
        ...params,
        sort_by: key,
        sort_order: sort,
      });
    }
    setExpanded({ id: null, key: "", state: false });
  }

  return (
    <div className={`table__wrapper ${wrapper_class}`}>
      <div className={`action__container ${action_container}`}>
        <div
          className={`action__container-start ${action_container_start_class}`}
        >
          {custom_pre_action_start}
          {enableAction && (
            <button
              type="button"
              className={`action__container-start-action ${action__container_start_action_class}`}
              onClick={handleActionClick}
            >
              {action__container_start_action_icon}
              <span>{action_container_start_action_label || "Add Action"}</span>
            </button>
          )}
          {custom_post_action_start}
        </div>
        <div className={`action__container-end ${action_container_end_class}`}>
          {custom_pre_action_end}
          {filterView ||
            (filter && (
              <div className="action__container-filter">
                <label
                  className={`action__container-filter-label ${filterLabelClass}`}
                >
                  <img
                    src="https://img.icons8.com/?size=10&id=JuOItFevAQev&format=png&color=000000"
                    alt="filter"
                  />
                  {filterLabel}
                </label>
                {filterList.length > 0 ? (
                  <ul className={`${filterListClass}`}>
                    {filterList.length > 0 &&
                      filterList.map((item, index) => (
                        <li
                          key={index}
                          onClick={() => onFilterSelect(item?.value, item)}
                          className={`${
                            filterValue === item?.value ? "active" : ""
                          }`}
                        >
                          {item?.label}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <div className="no__filter">
                    <span>No Filter</span>
                  </div>
                )}
              </div>
            ))}
          {searchView ||
            (search && (
              <div
                className={`action__container-search ${searchFieldClass} ${searchType} ${searchTrigger}`}
              >
                <input
                  type="text"
                  placeholder="Search"
                  value={searchString}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <img
                  src="https://img.icons8.com/?size=15&id=HNqs6ySvgu0d&format=png&color=000000"
                  alt="Search Icon"
                />
              </div>
            ))}
          {custom_post_action_end}
        </div>
      </div>
      <div className={`table__container-wrapper ${tableContainerWrapperClass}`}>
        <div
          className={`table__container ${tableType} ${tableClass} ${
            stripe ? "table__stripe" : ""
          }`}
        >
          {headers.length > 0 && (
            <div className={`table__header ${headerType} ${tableHeaderClass}`}>
              {headers.map((item, index) => {
                const ascActiveSortIcon =
                  "https://img.icons8.com/?size=14&id=8324&format=png&color=000000";
                const descActiveSortIcon =
                  "https://img.icons8.com/?size=14&id=11348&format=png&color=000000";
                const ascDeactiveSortIcon =
                  "https://img.icons8.com/?size=14&id=8324&format=png&color=777777";
                const descDeactiveSortIcon =
                  "https://img.icons8.com/?size=14&id=11348&format=png&color=777777";
                return (
                  <div
                    key={index}
                    className={`table__header-item ${
                      globalPlacement || item?.placement || "center"
                    } ${index === 0 && start_stick ? "start__stick" : ""} ${
                      index === headers.length - 1 && end_stick
                        ? "end__stick"
                        : ""
                    }`}
                    {...{
                      style: {
                        ...(item?.width && {
                          width: item?.width,
                          minWidth: item?.width,
                          maxWidth: item?.width,
                        }),
                      },
                    }}
                  >
                    {check && index === 0 && (
                      <div
                        className={`check__box ${
                          dataSource.every((data) =>
                            selectedRows.some(
                              (row) =>
                                row[selectMatchKey] === data[selectMatchKey]
                            )
                          ) && "checked"
                        }`}
                        onClick={() =>
                          dataSource.every((data) =>
                            selectedRows.some(
                              (row) =>
                                row[selectMatchKey] === data[selectMatchKey]
                            )
                          )
                            ? onRowSelect([])
                            : onRowSelect(dataSource)
                        }
                      />
                    )}
                    {item?.title}
                    {item?.sort &&
                      (paginationType === "inList" ? (
                        tempParams?.sort_order === "asc" ? (
                          <div className="sort__order">
                            <img
                              src={`${
                                item?.key === tempParams?.sort_by
                                  ? ascActiveSortIcon
                                  : ascDeactiveSortIcon
                              }`}
                              alt="Descending Sort"
                              onClick={() =>
                                handleSorting(item?.key || "", "desc")
                              }
                            />
                          </div>
                        ) : (
                          <div className="sort__order">
                            <img
                              src={`${
                                item?.key === tempParams?.sort_by
                                  ? descActiveSortIcon
                                  : descDeactiveSortIcon
                              }`}
                              alt="Ascending Sort"
                              onClick={() =>
                                handleSorting(item?.key || "", "asc")
                              }
                            />
                          </div>
                        )
                      ) : params?.sort_order === "asc" ? (
                        <div className="sort__order">
                          <img
                            src={`${
                              item?.key === tempParams?.sort_by
                                ? ascActiveSortIcon
                                : ascDeactiveSortIcon
                            }`}
                            alt="Descending Sort"
                            onClick={() =>
                              handleSorting(item?.key || "", "desc")
                            }
                          />
                        </div>
                      ) : (
                        <div className="sort__order">
                          <img
                            src={`${
                              item?.key === tempParams?.sort_by
                                ? descActiveSortIcon
                                : descDeactiveSortIcon
                            }`}
                            alt="Ascending Sort"
                            onClick={() =>
                              handleSorting(item?.key || "", "asc")
                            }
                          />
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          )}
          {loading ? (
            <div className="table__loading">
              {loadRender || (
                <div
                  className={`load__typing ${loaderClass}`}
                  role="status"
                  aria-live="polite"
                >
                  Loading...
                </div>
              )}
            </div>
          ) : Array.isArray(searchDataState) && searchDataState.length > 0 ? (
            searchDataState.map((item: SourceData, index) => (
              <React.Fragment key={index}>
                <div
                  key={index}
                  className="table__row"
                  onClick={() =>
                    selectedRows.some(
                      (row) => row[selectMatchKey] === item[selectMatchKey]
                    )
                      ? onRowSelect(
                          selectedRows.filter(
                            (row) =>
                              row[selectMatchKey] !== item[selectMatchKey]
                          )
                        )
                      : onRowSelect([...selectedRows, item])
                  }
                  {...(showMove
                    ? {
                        draggable: true,
                        onDragStart: (e: React.DragEvent<HTMLDivElement>) => {
                          e.dataTransfer.setData("rowIndex", index.toString());
                        },
                        onDragOver: (e: React.DragEvent<HTMLDivElement>) =>
                          e.preventDefault(),
                        onDrop: (e: React.DragEvent<HTMLDivElement>) => {
                          const from = Number(
                            e.dataTransfer.getData("rowIndex")
                          );
                          const to = index;
                          if (from === to) return;
                          const updatedOrder = [...searchDataState];
                          const [removed] = updatedOrder.splice(from, 1);
                          updatedOrder.splice(to, 0, removed);
                          const newOrder = updatedOrder.map((item) =>
                            dataSource.findIndex(
                              (row) =>
                                row[selectMatchKey] === item[selectMatchKey]
                            )
                          );
                          onUpdateOrder({ orderRows: updatedOrder, newOrder });
                          setSearchDataState(updatedOrder);
                        },
                      }
                    : {})}
                >
                  {headers.map((header, indx: number) => (
                    <div
                      key={indx}
                      className={`table__column ${
                        globalPlacement || header?.placement || "center"
                      } ${
                        header?.viewLines &&
                        header?.viewLines > 0 &&
                        clampColumnMessage
                      } ${header?.expand && "expand__column"}  ${
                        indx === 0 && start_stick ? "start__stick" : ""
                      } ${
                        indx === headers.length - 1 && end_stick
                          ? "end__stick"
                          : ""
                      }`}
                      {...{
                        style: {
                          ...(header?.viewLines &&
                            !header?.expand && {
                              display: "-webkit-box",
                              WebkitLineClamp: header?.viewLines, // number of lines
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: "1.2em",
                              // maxHeight:
                              //   header?.viewLines === 2
                              //     ? `calc(1.6em * ${header?.viewLines})`
                              //     : header?.viewLines > 2
                              //     ? `calc(1.5em * ${header?.viewLines})`
                              //     : "auto", // optional safety
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              boxSizing: "border-box",
                              whiteSpace:
                                header?.viewLines > 1 ? "normal" : "nowrap", // ✅ important for multi-line
                            }),
                          ...(header?.width && {
                            width: header?.width,
                            minWidth: header?.minWidth || header?.width,
                            maxWidth: header?.maxWidth || header?.width,
                          }),
                        },
                      }}
                    >
                      {check && indx === 0 && (
                        <div
                          className={`check__box ${
                            selectedRows.some(
                              (row) =>
                                row[selectMatchKey] === item[selectMatchKey]
                            ) && "checked"
                          }`}
                          onClick={() =>
                            selectedRows.some(
                              (row) =>
                                row[selectMatchKey] === item[selectMatchKey]
                            )
                              ? onRowSelect(
                                  selectedRows.filter(
                                    (row) =>
                                      row[selectMatchKey] !==
                                      item[selectMatchKey]
                                  )
                                )
                              : onRowSelect([...selectedRows, item])
                          }
                        />
                      )}
                      {header?.expand ? (
                        <p className="expand__text">
                          {header?.type === "html"
                            ? extractHTMLText(
                                item[header?.key as string] as string
                              )
                            : (item[header?.key as string] as ReactNode)}
                        </p>
                      ) : header?.type === "html" ? (
                        <span
                          style={
                            header?.viewLines && !header?.expand
                              ? {
                                  display: "-webkit-box",
                                  WebkitLineClamp: header?.viewLines,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  lineHeight: "1.2em",
                                  maxHeight:
                                    header?.viewLines === 2
                                      ? `calc(1.6em * ${header?.viewLines})`
                                      : header?.viewLines > 2
                                      ? `calc(1.5em * ${header?.viewLines})`
                                      : "auto",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                  whiteSpace:
                                    header?.viewLines > 1 ? "normal" : "nowrap",
                                }
                              : undefined
                          }
                        >
                          {extractHTMLText(
                            item[header?.key as string] as string
                          )}
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: header?.viewLines,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: "1.2em",
                            maxHeight:
                              header?.viewLines === 2
                                ? `calc(1.6em * ${header?.viewLines})`
                                : (header?.viewLines ?? 0) > 2
                                ? `calc(1.5em * ${header?.viewLines})`
                                : "auto",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            whiteSpace:
                              (header?.viewLines ?? 0) > 1
                                ? "normal"
                                : "nowrap",
                          }}
                        >
                          {item[header?.key as string] as ReactNode}
                        </span>
                      )}
                      {groupView && groupAction
                        ? indx === 0 &&
                          item?.groupRows &&
                          Array.isArray(item?.groupRows) &&
                          item?.groupRows.length > 0 && (
                            <img
                              src={
                                "https://img.icons8.com/?size=100&id=39786&format=png&color=000000"
                              }
                              width={12}
                              height={12}
                              alt="Arrow Icon"
                              className={`group-arrow ${groupArrowClass}`}
                              style={{
                                rotate: !groupViewPanel ? "0deg" : "90deg",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setGroupViewPanel(!groupViewPanel);
                                setGroupOpenIndex(index);
                              }}
                            />
                          )
                        : header?.expand && (
                            <img
                              src={
                                "https://img.icons8.com/?size=100&id=39786&format=png&color=000000"
                              }
                              alt={
                                expanded?.id === index && expanded?.state
                                  ? "Collapse"
                                  : "Expand"
                              }
                              className={
                                expanded?.id === index && expanded?.state
                                  ? "open_arrow"
                                  : "close_arrow"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(
                                  expanded?.id === index && expanded?.state
                                    ? {
                                        id: null,
                                        key: "",
                                        state: false,
                                      }
                                    : {
                                        id: index,
                                        key: header?.key as string,
                                        state: true,
                                      }
                                );
                              }}
                            />
                          )}
                    </div>
                  ))}
                </div>

                {groupViewPanel &&
                  (groupOpenIndex === index || !groupAction) &&
                  item?.groupRows &&
                  Array.isArray(item?.groupRows) &&
                  item?.groupRows.length > 0 &&
                  (groupRender ? (
                    groupRender(
                      item?.groupRows || [],
                      "group__view-panel active"
                    )
                  ) : (
                    <div
                      className={`group__view-panel ${
                        groupViewPanel &&
                        (groupOpenIndex === index || !groupAction)
                          ? "active"
                          : ""
                      } ${groupViewClass}`}
                    >
                      {item?.groupRows.map(
                        (groupItem: any, groupIndx: number) => (
                          <div key={groupIndx} className="table__row">
                            {Object.values(groupItem).map(
                              (rowItem: any, rowIndx: number) => (
                                <div
                                  key={rowIndx}
                                  className="table__column"
                                  style={{
                                    width: headers[rowIndx]?.width || "100%",
                                    minWidth: headers[rowIndx]?.width
                                      ? Number(headers[rowIndx]?.width) -
                                        groupColPadding
                                      : "auto",
                                    backgroundColor: "#F0F0F0",
                                  }}
                                >
                                  {groupCheck &&
                                  rowIndx === 0 &&
                                  selectedRows.filter(
                                    (rowItem) =>
                                      item[selectMatchKey] ===
                                      rowItem[selectMatchKey]
                                  ).length > 0 ? (
                                    <div
                                      className={`check__box ${
                                        selectedRows.some(
                                          (rowItem) =>
                                            item[selectMatchKey] ===
                                              rowItem[selectMatchKey] &&
                                            rowItem?.childRows &&
                                            Array.isArray(rowItem?.childRows) &&
                                            rowItem?.childRows.some(
                                              (childItem) =>
                                                groupItem[selectMatchKey] ===
                                                childItem[selectMatchKey]
                                            )
                                        )
                                          ? "checked"
                                          : ""
                                      }`}
                                      onClick={() => {
                                        let selectedItem = selectedRows.filter(
                                          (rowItem) =>
                                            item[selectMatchKey] ===
                                            rowItem[selectMatchKey]
                                        )[0];
                                        const itemIndex =
                                          selectedRows.findIndex(
                                            (rowItem) =>
                                              item[selectMatchKey] ===
                                              rowItem[selectMatchKey]
                                          );
                                        let groupRows: any = [];
                                        if (selectedItem?.childRows) {
                                          groupRows = selectedItem?.childRows;
                                        }

                                        if (selectedItem?.childRows) {
                                          if (
                                            groupRows.filter(
                                              (childItem: any) =>
                                                groupItem[selectMatchKey] ===
                                                childItem[selectMatchKey]
                                            ).length > 0
                                          ) {
                                            groupRows = groupRows.filter(
                                              (childItem: any) =>
                                                groupItem[selectMatchKey] !==
                                                childItem[selectMatchKey]
                                            );
                                          } else {
                                            groupRows.push(groupItem);
                                          }
                                          selectedItem = {
                                            ...selectedItem,
                                            childRows: groupRows,
                                          };
                                        } else {
                                          groupRows.push(groupItem);
                                          selectedItem = {
                                            ...selectedItem,
                                            childRows: groupRows,
                                          };
                                        }
                                        let updateSelectedRows = selectedRows;
                                        updateSelectedRows[itemIndex] =
                                          selectedItem;
                                        onRowSelect(updateSelectedRows);
                                        setRefresh(!refresh);
                                      }}
                                    />
                                  ) : null}
                                  {rowItem}
                                </div>
                              )
                            )}
                          </div>
                        )
                      )}
                    </div>
                  ))}

                {expanded?.state && (
                  <div
                    className={`expand__data ${
                      expanded?.id === index && expanded?.state
                        ? "expand"
                        : "collapse"
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: item[expanded?.key as string] as string,
                    }}
                  />
                )}
              </React.Fragment>
            ))
          ) : (
            <div className="no__data">No Data Found</div>
          )}
        </div>
      </div>
      {params && pagination && (
        <Pagination
          params={paginationType === "inList" ? tempParams : params}
          updateParams={handlePagination}
          paginationPlacement={paginationPlacement}
          paginationWrapperClass={paginationWrapperClass}
          paginationStyle={paginationStyle}
          prevText={prevText}
          nextText={nextText}
          prevIcon={prevIcon}
          nextIcon={nextIcon}
          paginationButtonClass={paginationButtonClass}
          pageOptions={pageOptions}
        />
      )}
    </div>
  );
}
